import { IBillingProvider } from './providers/IBillingProvider';
// Import GoogleBillingProvider - vite.config.ts aliases react-native-iap to a stub in web builds
// so this import won't cause build errors, but the provider will only be used in Android builds
import { GoogleBillingProvider } from './providers/GoogleBillingProvider';
import { AppleBillingProvider } from './providers/AppleBillingProvider';
import { detectPlatform } from './utils/platform-detector';
import { GOOGLE_PLAY_VERIFIED_PRODUCT_ID, APPLE_VERIFIED_PRODUCT_ID } from '@/config/subscriptions';
import type {
  BillingPlatform,
  BillingProduct,
  PurchaseResult,
  Subscription,
  RestoredPurchase,
  BillingEventListener,
  BillingEvent,
} from './types';
import type { PurchaseOptions } from './providers/IBillingProvider';

/**
 * Centralized billing manager using strategy pattern
 * V209.6: Paddle removed - only Google Play Billing on Android
 */
export class BillingManager {
  private static instance: BillingManager | null = null;
  private provider: IBillingProvider | null = null;
  private initialized = false;
  private eventListeners: Set<BillingEventListener> = new Set();

  private constructor() {
    const platform = detectPlatform();

    if (platform === 'android') {
      this.provider = new GoogleBillingProvider();
      console.log('[BillingManager] Using Google Play Billing');
      this.provider.addEventListener((event) => {
        this.notifyListeners(event);
      });
    } else if (platform === 'ios') {
      this.provider = new AppleBillingProvider();
      console.log('[BillingManager] Using Apple App Store Billing');
      this.provider.addEventListener((event) => {
        this.notifyListeners(event);
      });
    } else {
      // V209.6: Web has no billing provider (Paddle removed)
      console.log('[BillingManager] Billing disabled on web (Paddle removed)');
      this.provider = null;
    }
  }

  static getInstance(): BillingManager {
    if (!BillingManager.instance) {
      BillingManager.instance = new BillingManager();
    }
    return BillingManager.instance;
  }

  static resetInstance(): void {
    if (BillingManager.instance) {
      BillingManager.instance.dispose();
      BillingManager.instance = null;
    }
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      console.log('[BillingManager] Already initialized');
      return;
    }

    if (!this.provider) {
      console.log('[BillingManager] No billing provider available (web platform)');
      this.initialized = true; // Mark as initialized but disabled
      return;
    }

    console.log('[BillingManager] Initializing...');
    
    try {
      await this.provider.initialize();
      this.initialized = true;
      console.log('[BillingManager] Initialized successfully');
    } catch (error: any) {
      console.error('[BillingManager] Initialization failed:', error);
      // Surface a human-readable message; Capacitor "UNIMPLEMENTED" means the
      // native plugin is not yet registered (e.g. first install, needs rebuild).
      const code    = error?.code ?? '';
      const message = error?.message ?? JSON.stringify(error) ?? 'Unknown error';
      const friendly =
        code === 'UNIMPLEMENTED'
          ? `Native billing plugin not found. Please rebuild the app in Xcode.`
          : `Billing initialization failed: ${message}`;
      throw new Error(friendly);
    }
  }

  isReady(): boolean {
    return this.initialized && (this.provider?.isReady() ?? false);
  }

  getPlatform(): BillingPlatform | null {
    return this.provider?.platform || null;
  }

  async getProducts(): Promise<BillingProduct[]> {
    this.ensureInitialized();
    if (!this.provider) {
      throw new Error('Billing not available on this platform');
    }
    return this.provider.getProducts();
  }

  /**
   * Launch a purchase flow directly — used by the iOS path in VerifyPaywall which
   * needs the raw PurchaseResult (including the JWS token) to send to the server.
   * Android uses purchaseVerified() instead; this does NOT break that flow.
   */
  async launchPurchaseFlow(productId: string, options?: PurchaseOptions & { appAccountToken?: string }): Promise<PurchaseResult> {
    this.ensureInitialized();

    if (!this.provider) {
      return {
        success: false,
        error: {
          code: 'NOT_AVAILABLE',
          message: 'Billing not available on this platform',
          recoverable: false,
        },
      };
    }

    this.notifyListeners({
      type: 'purchase_started',
      platform: this.provider.platform,
      timestamp: new Date(),
    });

    try {
      const result = await this.provider.launchPurchaseFlow(productId, options);
      if (result.success) {
        this.notifyListeners({
          type: 'purchase_completed',
          platform: this.provider.platform,
          timestamp: new Date(),
          data: result,
        });
      } else {
        this.notifyListeners({
          type: 'purchase_failed',
          platform: this.provider.platform,
          timestamp: new Date(),
          data: result.error,
        });
      }
      return result;
    } catch (error: any) {
      this.notifyListeners({
        type: 'purchase_failed',
        platform: this.provider.platform,
        timestamp: new Date(),
        data: error,
      });
      return {
        success: false,
        error: {
          code: error?.code ?? 'UNKNOWN',
          message: error?.message ?? 'Purchase failed',
          recoverable: false,
        },
      };
    }
  }

  async purchaseVerified(): Promise<PurchaseResult> {
    this.ensureInitialized();

    if (!this.provider) {
      return {
        success: false,
        error: {
          code: 'NOT_AVAILABLE',
          message: 'Billing not available on this platform',
          recoverable: false,
        },
      };
    }

    console.log('🚀 V282.2: [BillingManager] Starting Verified purchase');
    console.log(`🚀 V282.2: [BillingManager] Expected product ID: ${GOOGLE_PLAY_VERIFIED_PRODUCT_ID}`);

    this.notifyListeners({
      type: 'purchase_started',
      platform: this.provider.platform,
      timestamp: new Date(),
    });

    try {
      const products = await this.provider.getProducts();

      console.log(`🚀 V282.2: [BillingManager] Returned ${products.length} products:`, products.map(p => p.id));

      // Use the correct product ID for the active platform
      const expectedProductId = this.provider.platform === 'apple'
        ? APPLE_VERIFIED_PRODUCT_ID
        : GOOGLE_PLAY_VERIFIED_PRODUCT_ID;

      // V282.2: Deterministic product selection - NO fuzzy matching
      const verifiedProduct = products.find(p => p.id === expectedProductId);

      if (!verifiedProduct) {
        const store = this.provider.platform === 'apple' ? 'App Store' : 'Google Play';
        const errorMsg = `Verified product not found in ${store} product list.\n` +
          `Expected: ${expectedProductId}\n` +
          `Returned: [${products.map(p => p.id).join(', ')}]\n` +
          `\n` +
          `💡 TROUBLESHOOTING:\n` +
          `1. App must be installed from ${store}\n` +
          `2. Product '${expectedProductId}' must be Active in ${store} console\n` +
          `3. Device must use a test account\n` +
          `4. Check your developer console for product status`;

        console.error(`❌ V282.2: [BillingManager]`, errorMsg);
        throw new Error(errorMsg);
      }

      console.log('✅ V282.2: [BillingManager] Found Verified product:', {
        id:         verifiedProduct.id,
        price:      verifiedProduct.localizedPrice || verifiedProduct.price,
        offerToken: verifiedProduct.offerToken ? 'present' : 'missing',
        platform:   this.provider.platform,
      });

      // V282.2: Pass offerToken if available (required for Google Play subscriptions with base plans)
      const result = await this.provider.launchPurchaseFlow(
        verifiedProduct.id,
        { offerToken: verifiedProduct.offerToken }
      );

      if (result.success) {
        console.log('✅ V282.2: [BillingManager] Purchase successful');
        this.notifyListeners({
          type: 'purchase_completed',
          platform: this.provider.platform,
          timestamp: new Date(),
          data: result,
        });
      } else {
        console.log('❌ V282.2: [BillingManager] Purchase failed:', result.error);
        this.notifyListeners({
          type: 'purchase_failed',
          platform: this.provider.platform,
          timestamp: new Date(),
          data: result.error,
        });
      }

      return result;
    } catch (error: any) {
      console.error('❌ V282.2: [BillingManager] Purchase error:', error);

      this.notifyListeners({
        type: 'purchase_failed',
        platform: this.provider.platform,
        timestamp: new Date(),
        data: error,
      });

      return {
        success: false,
        error: {
          code: 'UNKNOWN',
          message: error.message || 'Purchase failed',
          recoverable: false,
        },
      };
    }
  }

  async getActiveSubscription(): Promise<Subscription | null> {
    this.ensureInitialized();
    if (!this.provider) {
      return null;
    }
    const subscriptions = await this.provider.getActiveSubscriptions();
    return subscriptions[0] || null;
  }

  async getActiveSubscriptions(): Promise<Subscription[]> {
    this.ensureInitialized();
    if (!this.provider) {
      return [];
    }
    return this.provider.getActiveSubscriptions();
  }

  async restorePurchases(): Promise<RestoredPurchase[]> {
    this.ensureInitialized();
    
    if (!this.provider) {
      return [];
    }
    
    console.log('[BillingManager] Restoring purchases');
    this.notifyListeners({
      type: 'restore_started',
      platform: this.provider.platform,
      timestamp: new Date(),
    });

    try {
      const restored = await this.provider.restorePurchases();
      
      console.log('[BillingManager] Restored', restored.length, 'purchases');
      this.notifyListeners({
        type: 'restore_completed',
        platform: this.provider.platform,
        timestamp: new Date(),
        data: { count: restored.length },
      });

      return restored;
    } catch (error) {
      console.error('[BillingManager] Restore failed:', error);
      throw error;
    }
  }

  async syncWithServer(): Promise<void> {
    this.ensureInitialized();
    if (!this.provider) {
      return;
    }
    console.log('[BillingManager] Syncing with server');
    const subscriptions = await this.provider.getActiveSubscriptions();
    for (const subscription of subscriptions) {
      console.log('[BillingManager] Syncing subscription:', subscription.id);
    }
  }

  addEventListener(listener: BillingEventListener): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  dispose(): void {
    console.log('[BillingManager] Disposing');
    if (this.provider) {
      this.provider.dispose();
    }
    this.eventListeners.clear();
    this.initialized = false;
  }

  private ensureInitialized(): void {
    if (!this.initialized) {
      throw new Error('BillingManager not initialized. Call initialize() first.');
    }
  }

  private notifyListeners(event: BillingEvent): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('[BillingManager] Event listener error:', error);
      }
    });
  }
}

